import { Controller } from "@hotwired/stimulus";
import { useStore } from 'stimulus-store';
import { getModelsForSelect } from "shared/device_types/device_types";
import { getPartsForSelect } from "shared/device_types/parts";
import { updateSelect } from "shared/selects/select_helpers";
import { getBundle, getBundlesForSelect } from "shared/device_types/bundles";
import { isEmpty } from "lodash";
import { BUNDLE_SELECT_ID } from "constants/bundles_constants";
import TomSelect from "tom-select";
import $ from "jquery";
import { bundleStore } from "../../../stores/device_types/bundle_store";
import { allBundlesStore } from "../../../stores/device_types/all_bundles_store";

// Connects to data-controller="selects--device-types--bundle-select"
export default class extends Controller {

    static stores = [
        allBundlesStore,
        bundleStore
    ]

    // This defines the "paginationTokenValue" variable
    // https://stimulus.hotwired.dev/handbook/managing-state
    static values = {
        paginationToken: String
    }

    connect() {
        useStore(this);
    }

    initialize() {
        const selectElement = this.element;
        const bundleSelectId = this.element.id;

        getBundlesForSelect().then((results) => {
            const control = new TomSelect(selectElement, {
                placeholder: 'Select bundle',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'name',
                searchField: 'name',
                sortField: {
                    field: "name",
                    direction: "asc"
                },
                multiple: true,
                maxItems: 1, // This defines the number of items a user can select
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                onChange: (event) => this._bundleSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (event) => this._bundleUnselectedEvent(event)  // Keep this separate or page loads will be broken
            });

            // Add the expected options to the select. We're doing it this way instead of
            // in the constructor so the items appear in the underlying select. This makes
            // it possible to test using capybara matchers
            updateSelect(bundleSelectId, results);

            // Syncing will ensure options are stubbed out within the select element. This
            // ensures the tests will have the data in the DOM it can test against
            control.sync();

            // Store the control instance for later use
            this.tomSelectControl = control;

            // If a supplier already populated the store before TomSelect was ready, apply it now
            const existingBundles = this.allBundlesStoreValue;
            if (existingBundles && existingBundles.length > 0) {
                this.onAllBundlesStoreUpdate(existingBundles);
            }
        })
    }

    // Automatically called when allBundlesStore changes
    onAllBundlesStoreUpdate(newBundles) {
        if (!this.tomSelectControl) {
            return;
        }

        // Get currently selected value before clearing
        const currentValue = this.tomSelectControl.getValue();

        // Clear selected items first so TomSelect re-renders them with updated names
        this.tomSelectControl.clear(true);

        // Update both the underlying <select> DOM and TomSelect's internal state
        updateSelect(this.element.id, (newBundles || []).map(b => ({ id: b.id, name: b.name })));

        // Check if currently selected bundle still exists
        if (currentValue) {
            const stillExists = newBundles.some(b => b.id === currentValue);
            if (stillExists) {
                // Restore selection if bundle still exists
                this.tomSelectControl.setValue(currentValue, true);
            } else {
                // Clear selection if bundle was deleted
                this.tomSelectControl.clear();
                this.setBundleStoreValue({});
                
                // Dispatch cleared event
                window.dispatchEvent(new Event('bundleSelectionCleared', {
                    detail: {},
                    bubbles: true
                }));
            }
        }
    }

    _bundleSelectionChanged(event) {
        // Check if we want to omit events. Return if we do
        const omitEvents = this.element.className.includes('omit-change-events'); // This class name has to match what's passed into the partial
        if (omitEvents) {
            return;
        }

        // Only process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(event)) {
            return;
        }

        // Describe the selected bundle. We need to do this to
        getBundle(event).then((bundle) => {
            // Update store with bundle data
            this.setBundleStoreValue({
                id: bundle.id,
                name: bundle.name,
                icon: bundle.icon,
                models: bundle.models,
                parts: bundle.parts,
                description: bundle.description
            });

            // Send an event telling the name input, parts select, and model select
            // controllers to enable those elements. I stole this method from here:
            // https://lmika.org/2021/02/17/communication-among-stimulus.html
            window.dispatchEvent(new CustomEvent('bundleSelectionMade', {
                detail: {
                    id: bundle.id,
                    name: bundle.name,
                    text: bundle.name, // Keep for backward compatibility
                    icon: bundle.icon,
                    models: bundle.models,
                    parts: bundle.parts,
                    description: bundle.description
                },
                bubbles: true // This needs to be explicitly set to enable event bubbling
            }));
        });
    }

    _bundleUnselectedEvent(values) {
        // Clear the bundle store
        this.setBundleStoreValue({});

        // Dispatch existing event for backward compatibility
        window.dispatchEvent(new Event('bundleSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));
    }

    // EVERYTHING BELOW HERE IS DEAD CODE. I WANT TO SUPPORT PAGINATION FOR THESE SELECTS BUT IT'S REALLY HARD
    _decorateSelect() {
        const selectElement = document.getElementById(BUNDLE_SELECT_ID);
        const control = selectElement.tomselect;

        // If the select isn't a tomselect yet, make it one.
        if (control === undefined) {
            this._getPage().then(bundle_json => {
                return new TomSelect(selectElement, {
                    placeholder: 'Select bundle',
                    create: false,
                    allowClear: true,
                    valueField: 'id',
                    labelField: 'text',
                    searchField: 'text',
                    sortField: {
                        field: "text",
                        direction: "asc"
                    },
                    multiple: false,
                    maxItems: null,
                    maxOptions: null, // tom-select will truncate the select to 50 options by default
                    options: this._getOptions(bundle_json),
                    plugins: ['virtual_scroll'], // This enables pagination
                    // plugins: ['pagination'], // This enables pagination

                    firstUrl: function (query) {
                        return '/api/device_types/bundle/describe';
                    },
                    load: function (query, callback) {
                        const url = this.getUrl(query);

                        this._getPage().then(bundle_json => {
                            callback(this._getOptions(bundle_json));
                        })
                    },
                });
            });
        } else {
            return control;
        }
    }

    async _getPage() {
        return $.ajax({
            url: '/api/device_types/bundle/describe',
            dataType: 'json',
            data: {
                page_size: 20,
                pagination_token: this.paginationTokenValue
            }
        }).then((bundles_json) => {
            this.paginationTokenValue = bundles_json['pagination_token'];
            return bundles_json;
        });
    }

    _getOptions(json) {
        // Create a variable to save the output
        const options = [];

        // Gather the results from this query and stash them in the output object
        for (const index in json.bundles) {
            options.push({
                id: json.bundles[index].id,
                text: json.bundles[index].name
            });
        }

        return options;
    }

    _getBundlesForSelect(control, paginationToken) {
        $.ajax({
            url: '/api/device_types/bundle/describe', dataType: 'json', data: {
                pagination_token: paginationToken
            }
        }).promise().then(bundle_json => {
            // Create a variable to save the output
            const results = [];

            // Gather the results from this query and stash them in the output object
            for (const index in bundle_json.bundles) {
                results.push({
                    id: bundle_json.bundles[index].id,
                    text: bundle_json.bundles[index].name,
                    models: bundle_json.bundles[index].models,
                    parts: bundle_json.bundles[index].parts
                });
            }

            // Return the results we have
            return results;
        }).then(bundles => {
            // Syncing will ensure options are stubbed out within the select element. This
            // ensures the tests will have the data in the DOM it can test against
            control.sync();

            // Add the expected options to the select. We're doing it this way instead of
            // in the constructor so the items appear in the underlying select. This makes
            // it possible to test using capybara matchers
            updateSelect(control, bundles);
        }).catch(error => {
            console.error(error);
            throw new Error('Error fetching bundles. Please try again.');
        });
    }

    _getPartsForSelect(control) {
        const partOptionClass = { class: 'Parts' }
        getPartsForSelect().then((results) => {
            // Loop through each returned part and add the option class property
            results.forEach(optionHash => {
                Object.assign(optionHash, partOptionClass);
            });

            // Add these options to the select
            control.addOptions(results);
        });
    }

    _getModelsForSelect(control) {
        const modelOptionClass = { class: 'Models' }
        getModelsForSelect(null, null).then((results) => {
            // Loop through each returned model and add the option class property
            results.forEach(optionHash => {
                Object.assign(optionHash, modelOptionClass);
            });

            // Add these options to the select
            control.addOptions(results);
        });
    }

}